import React, {useState, useEffect, useRef, useMemo, useCallback} from "react";
import {inject, observer} from "mobx-react";
import {debounce} from "lodash";

import {LLScrollLoader, LLPreloader} from "../../../components";
import {llattempt, trans} from "../../../common/utils";
import api from "../../../common/api";
import {JackpotGroup} from "./jackpot-group";
import GamesRender from "./games-render";

let jackpotsDetailsInterval = null;

const gameGridColumns = () => {
    const w = window.innerWidth;
    if (w >= 1600) return 6;
    if (w >= 1200) return 5;
    if (w >= 944) return 4;
    if (w >= 768) return 3;
    return 2;
};

const SlotsCasinosGamesList = (
    {
        store,
        gamesType,
        currentView,
        filtering,
        activeGameMenuItem,
        setActiveGameMenuItem,
        activeSubSubMenuItem,
        setActiveSubSubMenuItem,
        activeJackpot,
        isProviderWithJackpot = false,
        noJackpotCards = false
    }
) => {
    const viewSize = store.site.status.viewSize,
        mobileUserAgent = store.user.mobileUserAgent,
        customerGroup = store.user.customerGroup,
        slotsCasinoGames = store.slotsCasinoGames,
        activeProvider = store.slotsCasinoGames.presetFilters.activeProvider,
        getActiveProviderStoreItem = store.slotsCasinoGames.getActiveProviderStoreItem,
        jackpotGroupsList = store.slotsCasinoGames.jackpotGroupsList,
        slotsFiltersLoaded = store.slotsCasinoGames.slotsFiltersLoaded,
        jackpotGroupsListByProvider = store.slotsCasinoGames.jackpotGroupsListByProvider,
        jackpotGroupsListByActiveProvider =
            store.slotsCasinoGames.jackpotGroupsListByActiveProvider,
        getJackpotDetails = store.slotsCasinoGames.getJackpotDetails,
        providerJackpots = store.slotsCasinoGames.providerJackpots;

    const [isGamesListReloading, setGamesListReloading] = useState(true);
    const [isFetching, setFetching] = useState(false); //fetch flag
    const [itemsInRow, setItemsInRow] = useState(0); //items in row
    const [loading, setLoading] = useState(true); //loading on scroll
    const [games, setGames] = useState([]); //received games
    const [loadedGamesCount, setLoadedGamesCount] = useState(0); //total received games count after render or change activeItem
    const [allGamesLoaded, setAllGamesLoaded] = useState(false); //stop load more games on scroll event if we received games count less than we asked

    const gameListRef = useRef();

    /********************* Game getter **********************/
    const loadGames = (params) => {
        return llattempt(
            () =>
                api.games.getGames({
                    platform: mobileUserAgent ? 'mobile' : 'desktop',
                    type: gamesType === 'slots' ? 'slot' : 'casino',
                    params: JSON.stringify(params),
                }),
            {
                msg: 'Cant load games',
                at: 'SlotsCasinosGamesList.loadMoreGames',
                withParams: {
                    platform: mobileUserAgent ? 'mobile' : 'desktop',
                    type: gamesType === 'slots' ? 'slot' : 'casino',
                    params: params,
                },
                onError: () => {
                    setLoading(false);
                    setGamesListReloading(false);
                    setFetching(false);
                },
                withNotifier: false,
            }
        );
    };

    const loadMoreGames = (isRowMissingItems = true) => {
        if (!allGamesLoaded) {
            setFetching(true);
            let take = itemsInRow * 5; //game count for request

            if (isRowMissingItems && loadedGamesCount) {
                //we already have at least the first screenful loaded;
                //only fetch if the grid grew and we're actually short
                take = take > loadedGamesCount ? take - loadedGamesCount : 0;
            }

            const params = {
                skip: loadedGamesCount,
                take: take,
                filter: !activeJackpot
                    ? filtering.filter
                    : [[...filtering.filter], ['jackpotId', '=', activeJackpot]],
                sort: filtering.sort,
                select: filtering.select,
            };

            if (take !== 0) {
                setLoading(true);
                loadGames(params).then((result) => {
                    if (result.success) {
                        setGames((games) => games.concat(result.data.data || []));
                        setLoadedGamesCount(loadedGamesCount + result.data.data.length);
                        result.data.data.length < take && setAllGamesLoaded(true);
                    } else {
                        setAllGamesLoaded(true);
                    }
                    setLoading(false);
                    setGamesListReloading(false);
                    setFetching(false);
                });
            } else {
                setLoading(false);
                setGamesListReloading(false);
                setFetching(false);
            }
        }
    };

    const loadJackpotGames = () => {
        setFetching(true);
        let take = viewSize === 'mobile' ? itemsInRow : (itemsInRow - 1) * 2;

        if (jackpotGroupsList.length) {
            if (take !== 0) {
                const requests = [];
                const jGroups = activeProvider
                    ? jackpotGroupsListByActiveProvider
                    : jackpotGroupsList;
                setLoading(true);

                jGroups.forEach((group) => {
                    const params = {
                        skip: 0,
                        take: take,
                        filter: !activeJackpot
                            ? [filtering.filter, ['jackpotId', '=', group.id]]
                            : filtering.filter,
                        sort: filtering.sort,
                        select: filtering.select,
                    };
                    if (
                        activeProvider &&
                        !JSON.stringify(params.filter).includes(
                            JSON.stringify(['BrandName', '=', activeProvider])
                        )
                    ) {
                        params.filter = [
                            filtering.filter,
                            ['BrandName', '=', activeProvider],
                        ];
                    }
                    requests.push(loadGames(params));
                });
                Promise.all(requests).then((res) => {
                    res.forEach((result) => {
                        if (result.success) {
                            setGames((games) => games.concat(result.data.data || []));
                        }
                    });
                    setLoading(false);
                    setGamesListReloading(false);
                    setFetching(false);
                });
            }
        } else {
            if (!jackpotGroupsListByProvider.length) {
                setLoading(false);
                setGamesListReloading(false);
            }
            setFetching(false);
        }
    };
    /*************** call games getter on render or change activeItem or resize window ****************/
    const resetItemsInRow = useCallback(() => {
        const next = gameGridColumns();
        setItemsInRow((prev) => (prev !== next ? next : prev));
    }, []);

    const debouncedResetItemsInRow = useMemo(
        () => debounce(resetItemsInRow, 300, { leading: false, trailing: true }),
        [resetItemsInRow]
    );

    useEffect(() => {
        window.addEventListener('resize', debouncedResetItemsInRow);
        return () => {
            window.removeEventListener('resize', debouncedResetItemsInRow);
            debouncedResetItemsInRow.cancel();
        };
    }, [debouncedResetItemsInRow]);

    useEffect(() => {
        return () => clearInterval(jackpotsDetailsInterval);
    }, []);

    useEffect(() => {
        getJackpotDetails({ jackpotId: activeJackpot });
        clearInterval(jackpotsDetailsInterval);
        jackpotsDetailsInterval = setInterval(() => {
            getJackpotDetails({ jackpotId: activeJackpot });
        }, 10000);
    }, [activeJackpot]);
    /**************** ******************/

    /**************** reset state on change filter condition or change type or device type ******************/
    useEffect(() => {
        setGames([]);
        setLoadedGamesCount(0);
        setAllGamesLoaded(false);
        setGamesListReloading(true);
        resetItemsInRow();
    }, [
        gamesType,
        JSON.stringify(filtering.filter),
        JSON.stringify(filtering.sort),
        activeProvider,
        activeJackpot,
        JSON.stringify(currentView),
    ]);
    /**************** ******************/

    useEffect(() => {
        if (!itemsInRow) return; //wait for the grid to be measured before fetching
        if (
            !isFetching &&
            (!currentView.includes('provider') ||
                (getActiveProviderStoreItem(gamesType) &&
                    !getActiveProviderStoreItem(gamesType)?.isLoading))
        ) {
            if (
                (!currentView.includes('jackpots') && !isProviderWithJackpot) ||
                activeJackpot
            ) {
                loadMoreGames(!activeJackpot);
            } else {
                setGames([]);
                loadJackpotGames();
            }
        }
    }, [
        itemsInRow,
        customerGroup,
        slotsFiltersLoaded,
        isGamesListReloading,
        jackpotGroupsListByProvider,
    ]);

    /**************** RENDER ******************/
    return (
        <div className="games__list" ref={gameListRef}>
            {(activeGameMenuItem || activeSubSubMenuItem) && (
                <div
                    className="game-list__back-drop"
                    onClick={() => {
                        setActiveGameMenuItem('');
                        setActiveSubSubMenuItem(null);
                    }}
                />
            )}
            {isGamesListReloading ? (
                <div className="games__list-reloader">
                    <LLPreloader />
                </div>
            ) : (
                <LLScrollLoader
                    pageSize={loadedGamesCount}
                    loadingCallback={() => {
                        loadMoreGames(false);
                    }}
                >
                    {games.length && slotsCasinoGames[gamesType + 'FiltersLoaded'] ? (
                        currentView.includes('jackpots') || isProviderWithJackpot ? (
                            (activeProvider
                                ? jackpotGroupsListByActiveProvider
                                : jackpotGroupsList
                            ).map((group, i) => {
                                if (!activeJackpot || activeJackpot == group.id) {
                                    return (
                                        <JackpotGroup
                                            key={group.id}
                                            games={games}
                                            group={group}
                                            gamesType={gamesType}
                                            activeProvider={activeProvider}
                                            activeJackpot={activeJackpot}
                                            isGroupFirst={i === 0}
                                            jackpotGroupsListByProvider={
                                                activeProvider
                                                    ? jackpotGroupsListByActiveProvider
                                                    : jackpotGroupsListByProvider
                                            }
                                            takeItemsCount={
                                                viewSize === 'mobile'
                                                    ? itemsInRow
                                                    : (itemsInRow - 1) * 2
                                            }
                                        />
                                    );
                                }
                            })
                        ) : (
                            <GamesRender
                                jackpotGroups={noJackpotCards ? [] : providerJackpots({
                                    name: activeProvider,
                                })}
                                list={games}
                                itemsInRow={itemsInRow}
                                gamesType={gamesType}
                                isCentered={
                                    currentView.includes('featured') ||
                                    currentView.includes('new') ||
                                    currentView.includes('popular')
                                }
                            />
                        )
                    ) : loading ||
                      isFetching ||
                      !slotsCasinoGames[gamesType + 'FiltersLoaded'] ? null : (
                        <p className="games__list--empty">
                            {trans('No games matching these parameters')}
                        </p>
                    )}
                    {loading && <LLPreloader className="games__list-loader" />}
                </LLScrollLoader>
            )}
        </div>
    );
};

export default inject('store')(observer(SlotsCasinosGamesList));
